import DataLoader from "@-/com.npmjs.dataloader";
import memoize from "@-/com.npmjs.memoize";
import { scheduleDataloaderBatch } from "#schedule-dataloader-batch";
import type { BatchLoadFn } from "#create-dataloader";

const DEFAULT_MAX_BATCH_SIZE = 64;

/**
 * Coalesce `.load(key)` calls across React commits within one animation frame
 * (≈16ms on 60fps), instead of just within a single microtask tick. Mirrors
 * `createDataloader`'s scheduler — see that file for the full rationale.
 */
function getBatchScheduleFn(): (callback: () => void) => void {
  return scheduleDataloaderBatch;
}

/**
 * A DataLoader wrapper that is safe to embed in a TanStack Query `queryKey`.
 *
 * A raw `DataLoader` cannot go in a key: `hashKey` (React Query's deterministic
 * `JSON.stringify`) walks the loader's mutating internal `_batch` field, so a
 * queued `.load()` mutates the serialization and the key hashes to a *new*
 * string every tick — a refetch storm. This wrapper overrides `toJSON()` so the
 * loader hashes to a **stable string that still encodes the db identity** it was
 * built for, so:
 *   - the hash is constant across `.load()` calls (no storm), and
 *   - a loader built for a *different* db handle hashes *differently*, so an
 *     actor-DB swap (account switch, dev-stack churn) doesn't bleed one
 *     database's rows into the other's cache entry.
 */
export type QueryKeyStableDataloader<K, V> = DataLoader<K, V> & {
  toJSON: () => string;
};

type DependencyScope = {
  forDependency: (dependency: object) => DependencyScope;
};

/** A weak identity trie: fresh tuples with the same objects reach the same leaf. */
function createDependencyScope(): DependencyScope {
  return {
    forDependency: memoize((_dependency: object) => createDependencyScope(), {
      cache: new WeakMap(),
    }),
  };
}

export function createUseDataloader<K, V>({
  options,
  useLoader,
}: {
  options?: { maxBatchSize?: number };
  useLoader: () => {
    batchLoad: BatchLoadFn<K, V>;
    /**
     * Every stable database/client dependency the batch function reads, in a
     * fixed order. Tuple identity does not matter; its members' identities do.
     *
     * Different hook instances have different callback identities even with the
     * same dependencies. React may also discard pre-commit callbacks on Suspense.
     * Sharing by these dependencies coalesces all consumers and survives retries.
     * The batch function must not capture changing state absent from this tuple.
     */
    cacheIdentity: readonly [object, ...object[]];
  };
}): () => QueryKeyStableDataloader<K, V> {
  const maxBatchSize = options?.maxBatchSize ?? DEFAULT_MAX_BATCH_SIZE;
  const batchScheduleFn = getBatchScheduleFn();
  const dependencyScope = createDependencyScope();
  let nextLoaderTag = 0;

  // memoize keys by its first argument: the complete dependency tuple's stable
  // leaf, never the render-bound callback. Weak edges let replaced databases and
  // their loaders disappear together once no query or component uses them.
  const getLoader = memoize(
    (_scope: DependencyScope, batchLoad: BatchLoadFn<K, V>): QueryKeyStableDataloader<K, V> => {
      const tag = `dataloader-${nextLoaderTag++}`;
      return Object.assign(
        new DataLoader<K, V>(async (keys) => batchLoad(keys), {
          batchScheduleFn,
          cache: false,
          maxBatchSize,
        }),
        { toJSON: () => tag },
      );
    },
    { cache: new WeakMap() },
  );

  return function useDataloader(): QueryKeyStableDataloader<K, V> {
    const { batchLoad, cacheIdentity } = useLoader();
    let scope = dependencyScope;
    for (const dependency of cacheIdentity) {
      scope = scope.forDependency(dependency);
    }
    return getLoader(scope, batchLoad);
  };
}
